import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, IconButton, Tooltip, Menu, MenuItem, Checkbox, FormControlLabel, Snackbar, Alert,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LockIcon from '@mui/icons-material/Lock';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import StarIcon from '@mui/icons-material/Star';
import {
  alterarProtegerCodigoConsumidorDoProcessoService,
  findInformacoesAdicionaisInpescaoProcessoService,
} from '../../../../services/core/processos/processosService';
import * as C from '../../../theme/colors.constants';

const NAO_PERMITE_COBRANCA_PRESENCIAL = 'Não permite Cobrança Presencial';
const MENSAGEM_ERRO_CODIGO_CONSUMIDOR = 'Não foi possível alterar o Código do Consumidor.';
const CINZA_INATIVO = '#e4e4e4';
const BORDA_INATIVA = '#cecece';

/**
 * Espelha `SinalizacaoIcones.jsx` (tela real de Inspecionar Processo, AW2-911),
 * excluindo "Incluir na Produção" (fora dos critérios de aceite do ticket).
 *
 * `processoExpirado`: divergência preservada do real — o ÍCONE reage a
 * `statusExpiracao` (tri-state: ausente | PRESTES_A_EXPIRAR | EXPIRADO), mas o
 * CHECKBOX do dropdown reage a um campo diferente, `processoExpirado`
 * (booleano). Confirmado lendo `SinalizacaoIcones.jsx`/`InspecionarProcessoStyles.jsx`
 * linha a linha — não é um bug introduzido aqui, é o comportamento real
 * portado como está (AW2-911 pede "mesmo comportamento", não correção).
 */
const MARCADORES = [
  {
    id: 'codigoConsumidor',
    Icon: LockIcon,
    ativoIcone: q => Boolean(q?.codigoConsumidor),
    tooltipIcone: () => 'Código do Consumidor',
    cor: () => '#c156d2',
    label: 'Código do Consumidor',
    ativoCheckbox: q => Boolean(q?.codigoConsumidor),
    editavel: true,
  },
  {
    id: 'permiteCobrancaPresencial',
    Icon: DirectionsCarIcon,
    ativoIcone: q => !q?.permiteCobrancaPresencial,
    tooltipIcone: () => NAO_PERMITE_COBRANCA_PRESENCIAL,
    cor: () => '#79df49',
    label: NAO_PERMITE_COBRANCA_PRESENCIAL,
    ativoCheckbox: q => !q?.permiteCobrancaPresencial,
    editavel: false,
  },
  {
    id: 'processoExpirado',
    Icon: SentimentDissatisfiedIcon,
    ativoIcone: q => Boolean(q?.statusExpiracao),
    tooltipIcone: q => q?.hintStatusProcesso || '',
    cor: (q) => {
      if (q?.statusExpiracao === 'PRESTES_A_EXPIRAR') {
        return '#ff7a00';
      }
      if (q?.statusExpiracao === 'EXPIRADO') {
        return '#e7626a';
      }
      return CINZA_INATIVO;
    },
    label: 'Processo Expirado',
    ativoCheckbox: q => Boolean(q?.processoExpirado),
    editavel: false,
  },
  {
    id: 'clienteInativo',
    Icon: PersonOffIcon,
    ativoIcone: q => Boolean(q?.clienteInativo),
    tooltipIcone: () => 'Cliente Inativo',
    cor: () => '#3565c3',
    label: 'Cliente Inativo',
    ativoCheckbox: q => Boolean(q?.clienteInativo),
    editavel: false,
  },
  {
    id: 'recomendacaoEspecial',
    Icon: StarIcon,
    ativoIcone: q => Boolean(q?.recomendacaoEspecial),
    tooltipIcone: () => 'Recomendação Especial',
    cor: () => '#ffbd5a',
    label: 'Recomendação Especial',
    ativoCheckbox: q => Boolean(q?.recomendacaoEspecial),
    editavel: false,
  },
];

function MarcadoresIconesProcesso({
  idProcesso,
  sinalizacaoClienteProcessoQuery,
  podeModificarFase,
  setSinalizacaoInfo,
}) {
  const [anchor, setAnchor] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function onChangeCodigoConsumidor(event) {
    const { checked } = event.target;

    if (!idProcesso) {
      return;
    }

    try {
      setSalvando(true);
      await alterarProtegerCodigoConsumidorDoProcessoService(idProcesso, checked);
      const { data } = await findInformacoesAdicionaisInpescaoProcessoService(idProcesso);
      setSinalizacaoInfo(data);
    } catch {
      setErro(MENSAGEM_ERRO_CODIGO_CONSUMIDOR);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {MARCADORES.map((marcador) => {
          const ativo = marcador.ativoIcone(sinalizacaoClienteProcessoQuery);
          const cor = ativo ? marcador.cor(sinalizacaoClienteProcessoQuery) : CINZA_INATIVO;
          const { Icon } = marcador;
          return (
            <Tooltip
              key={marcador.id}
              title={ativo ? marcador.tooltipIcone(sinalizacaoClienteProcessoQuery) : ''}
              arrow
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  mx: 0.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${ativo ? cor : BORDA_INATIVA}`,
                  backgroundColor: cor,
                }}
              >
                <Icon sx={{ fontSize: 11, color: '#fff' }} />
              </Box>
            </Tooltip>
          );
        })}
        <Tooltip title="Sinalizadores" arrow>
          <IconButton
            size="small"
            aria-label="Sinalizadores"
            onClick={e => setAnchor(e.currentTarget)}
            sx={{ color: C.navy, ml: -0.5 }}
          >
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {MARCADORES.map(marcador => (
          <MenuItem key={marcador.id}>
            <FormControlLabel
              label={marcador.label}
              control={(
                <Checkbox
                  checked={marcador.ativoCheckbox(sinalizacaoClienteProcessoQuery)}
                  disabled={marcador.editavel ? !podeModificarFase || salvando : true}
                  onChange={marcador.editavel ? onChangeCodigoConsumidor : undefined}
                />
              )}
            />
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

MarcadoresIconesProcesso.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sinalizacaoClienteProcessoQuery: PropTypes.shape({
    codigoConsumidor: PropTypes.bool,
    permiteCobrancaPresencial: PropTypes.bool,
    statusExpiracao: PropTypes.string,
    hintStatusProcesso: PropTypes.string,
    processoExpirado: PropTypes.bool,
    clienteInativo: PropTypes.bool,
    recomendacaoEspecial: PropTypes.bool,
  }),
  podeModificarFase: PropTypes.bool,
  setSinalizacaoInfo: PropTypes.func.isRequired,
};

MarcadoresIconesProcesso.defaultProps = {
  idProcesso: null,
  sinalizacaoClienteProcessoQuery: null,
  podeModificarFase: false,
};

export default React.memo(MarcadoresIconesProcesso);
